import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
    CheckCircle, 
    Clock, 
    Download, 
    FileText, 
    AlertCircle,
    XCircle,
    Receipt,
    RefreshCw
} from 'lucide-react';
import { orderSignatureService, OrderSignature } from '../../services/orderSignatureService';
import { ncaLayerService, SignatureResult } from '../../services/ncaLayerService';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/auth';

const OrderSignaturePage: React.FC = () => {
    const { orderSignatureId } = useParams<{ orderSignatureId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [orderSignature, setOrderSignature] = useState<OrderSignature | null>(null);
    const [loading, setLoading] = useState(true);
    const [signing, setSigning] = useState(false);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [ncaLayerStatus, setNcaLayerStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
    const [certificates, setCertificates] = useState<string[]>([]);
    const [selectedCertificate, setSelectedCertificate] = useState<string>('');
    const [certificatePassword, setCertificatePassword] = useState('');

    useEffect(() => {
        loadOrderSignature();
        checkNcaLayer();
    }, [orderSignatureId]);

    const loadOrderSignature = async () => {
        if (!orderSignatureId) return;

        try {
            setLoading(true);
            const data = await orderSignatureService.getOrderSignature(parseInt(orderSignatureId));
            setOrderSignature(data);
        } catch (error: any) {
            console.error('Ошибка загрузки заявки:', error);
            toast.error(error.message || 'Ошибка загрузки заявки');
        } finally {
            setLoading(false);
        }
    };

    const checkNcaLayer = async () => {
        try {
            setNcaLayerStatus('checking');
            const isAvailable = await ncaLayerService.isAvailable();
            setNcaLayerStatus(isAvailable ? 'available' : 'unavailable');

            if (isAvailable) {
                // Загружаем список сертификатов
                const certs = await ncaLayerService.getCertificates();
                const certPaths = certs.map(cert => cert.path || cert.alias || 'PKCS12');
                setCertificates(certPaths);
                if (certPaths.length > 0) {
                    setSelectedCertificate(certPaths[0]);
                }
            }
        } catch (error) {
            console.error('Ошибка проверки NCALayer:', error);
            setNcaLayerStatus('unavailable');
        }
    };

    const handleDownloadPdf = async () => {
        if (!orderSignature) return;

        try {
            toast.loading('Скачивание заявки...', { id: 'download' });
            await orderSignatureService.downloadOrderSignaturePdf(orderSignature.id, orderSignature.orderId);
            toast.success('Заявка скачана!', { id: 'download' });
        } catch (error: any) {
            console.error('Ошибка скачивания заявки:', error);
            toast.error(error.message || 'Ошибка скачивания заявки', { id: 'download' });
        }
    };

    const handleDownloadInvoice = async () => {
        if (!orderSignature) return;

        try {
            toast.loading('Скачивание счёта...', { id: 'download-invoice' });
            await orderSignatureService.downloadInvoice(orderSignature.id, orderSignature.orderId);
            toast.success('Счёт скачан!', { id: 'download-invoice' });
        } catch (error: any) {
            console.error('Ошибка скачивания счёта:', error);
            toast.error(error.message || 'Ошибка скачивания счёта', { id: 'download-invoice' });
        }
    };

    const handleRefetchInvoice = async () => {
        if (!orderSignature) return;

        try {
            setLoadingInvoice(true);
            toast.loading('Запрос счёта из 1С...', { id: 'refetch-invoice' });
            await orderSignatureService.refetchInvoice(orderSignature.id);
            
            // Перезагружаем данные заявки
            const updatedSignature = await orderSignatureService.getOrderSignature(orderSignature.id);
            setOrderSignature(updatedSignature);
            
            toast.success('Счёт успешно получен!', { id: 'refetch-invoice' });
        } catch (error: any) {
            console.error('Ошибка запроса счёта:', error);
            toast.error(error.message || 'Ошибка запроса счёта', { id: 'refetch-invoice' });
        } finally {
            setLoadingInvoice(false);
        }
    };

    const handleSignOrder = async () => {
        if (!orderSignature) return;

        try {
            setSigning(true);

            // Проверяем доступность NCALayer
            if (ncaLayerStatus !== 'available') {
                toast.error('NCALayer недоступен. Установите NCALayer и запустите его.');
                return;
            }

            // Используем выбранный сертификат или PKCS12 по умолчанию
            const certPath = selectedCertificate || 'PKCS12';

            // Подготавливаем данные для подписания
            const orderData = JSON.stringify({
                orderSignatureId: orderSignature.id,
                orderId: orderSignature.orderId,
                companyBin: orderSignature.companyBin,
                timestamp: new Date().toISOString()
            });

            toast.loading('Подписание заявки...', { id: 'signing' });

            // Подписываем данные через NCALayer
            const signatureResult: SignatureResult = await ncaLayerService.signData(
                orderData,
                certPath,
                certificatePassword
            );

            // Отправляем подпись на сервер
            const isBuyer = user?.role === UserRole.CLIENT;
            const signRequest = {
                signatureData: signatureResult.signature,
                certificateData: signatureResult.certificate,
                signerName: signatureResult.signerInfo.commonName,
                organizationName: signatureResult.signerInfo.organization,
            };

            const signedOrder = isBuyer
                ? await orderSignatureService.signByBuyer(orderSignature.id, signRequest)
                : await orderSignatureService.signBySupplier(orderSignature.id, signRequest);

            toast.success('Заявка успешно подписана!', { id: 'signing' });
            setOrderSignature(signedOrder);
            
            // Очищаем пароль
            setCertificatePassword('');

        } catch (error: any) {
            console.error('Ошибка подписания заявки:', error);
            toast.error(error.message || 'Ошибка подписания заявки', { id: 'signing' });
        } finally {
            setSigning(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'DRAFT':
                return <Clock className="w-5 h-5 text-gray-500" />;
            case 'PENDING_SIGNATURE':
                return <Clock className="w-5 h-5 text-blue-500" />;
            case 'SIGNED_BY_BUYER':
                return <AlertCircle className="w-5 h-5 text-orange-500" />;
            case 'SIGNED_BY_SUPPLIER':
                return <AlertCircle className="w-5 h-5 text-orange-500" />;
            case 'SIGNED_BY_BOTH':
                return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'CANCELLED':
                return <XCircle className="w-5 h-5 text-red-500" />;
            default:
                return <FileText className="w-5 h-5 text-gray-500" />;
        }
    };

    const canSign = (): boolean => {
        if (!orderSignature || !user) return false;
        return orderSignatureService.needsSignature(orderSignature, user.role);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загрузка заявки...</p>
                </div>
            </div>
        );
    }

    if (!orderSignature) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Заявка не найдена</h2>
                    <button
                        onClick={() => navigate('/client/orders')}
                        className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Вернуться к заказам
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {getStatusIcon(orderSignature.status)}
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Заявка на поставку № {orderSignature.orderId}
                                </h1>
                                <p className="text-sm text-gray-600 mt-1">
                                    Статус: <span className="font-semibold">{orderSignature.statusDisplay}</span>
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleDownloadPdf}
                            disabled={!orderSignature.generatedPdfPath}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            <Download className="w-5 h-5" />
                            <span>Скачать PDF</span>
                        </button>
                    </div>
                </div>

                {/* Информация о заявке */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Информация о заявке</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-gray-600">Компания</p>
                            <p className="font-medium text-gray-900">{orderSignature.companyName}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">БИН</p>
                            <p className="font-medium text-gray-900">{orderSignature.companyBin}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Создано</p>
                            <p className="font-medium text-gray-900">
                                {new Date(orderSignature.createdAt).toLocaleString('ru-RU')}
                            </p>
                        </div>
                        {orderSignature.buyerSignedAt && (
                            <div>
                                <p className="text-sm text-gray-600">Подписано покупателем</p>
                                <p className="font-medium text-gray-900">
                                    {new Date(orderSignature.buyerSignedAt).toLocaleString('ru-RU')}
                                </p>
                            </div>
                        )}
                        {orderSignature.supplierSignedAt && (
                            <div>
                                <p className="text-sm text-gray-600">Подписано поставщиком</p>
                                <p className="font-medium text-gray-900">
                                    {new Date(orderSignature.supplierSignedAt).toLocaleString('ru-RU')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Статус подписания */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Статус подписания</h2>
                    <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                            {orderSignature.isSignedByBuyer ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                                <Clock className="w-5 h-5 text-gray-400" />
                            )}
                            <span className={`font-medium ${orderSignature.isSignedByBuyer ? 'text-green-600' : 'text-gray-600'}`}>
                                Покупатель
                            </span>
                        </div>
                        <div className="flex items-center space-x-3">
                            {orderSignature.isSignedBySupplier ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                                <Clock className="w-5 h-5 text-gray-400" />
                            )}
                            <span className={`font-medium ${orderSignature.isSignedBySupplier ? 'text-green-600' : 'text-gray-600'}`}>
                                Поставщик
                            </span>
                        </div>
                    </div>
                </div>

                {/* Счёт на оплату - показываем только когда подписана обеими сторонами */}
                {orderSignature.isSigned && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                            <Receipt className="w-5 h-5 text-blue-600" />
                            <span>Счёт на оплату</span>
                        </h2>
                        
                        {orderSignature.hasInvoice || orderSignature.invoicePdfPath ? (
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                    <span className="text-green-600 font-medium">Счёт получен</span>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={handleDownloadInvoice}
                                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        <Download className="w-5 h-5" />
                                        <span>Скачать счёт</span>
                                    </button>
                                    <button
                                        onClick={handleRefetchInvoice}
                                        disabled={loadingInvoice}
                                        className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                    >
                                        <RefreshCw className={`w-5 h-5 ${loadingInvoice ? 'animate-spin' : ''}`} />
                                        <span>Обновить</span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <Clock className="w-5 h-5 text-orange-500" />
                                    <span className="text-orange-600 font-medium">Счёт ещё не получен</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Счёт на оплату будет получен автоматически из системы 1С.
                                </p>
                                <button
                                    onClick={handleRefetchInvoice}
                                    disabled={loadingInvoice}
                                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-5 h-5 ${loadingInvoice ? 'animate-spin' : ''}`} />
                                    <span>{loadingInvoice ? 'Запрос...' : 'Запросить счёт'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Форма подписания */}
                {canSign() && (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Подписание заявки</h2>
                        
                        {ncaLayerStatus === 'checking' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-500 border-t-transparent mr-3"></div>
                                    <p className="text-yellow-800">
                                        Проверка подключения к NCALayer...
                                    </p>
                                </div>
                            </div>
                        )}

                        {ncaLayerStatus === 'unavailable' && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                <p className="text-red-800 mb-3">
                                    NCALayer недоступен. Пожалуйста, установите и запустите NCALayer для подписания заявки.
                                </p>
                                <button
                                    onClick={checkNcaLayer}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                                >
                                    Проверить снова
                                </button>
                            </div>
                        )}

                        {ncaLayerStatus === 'available' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Сертификат
                                    </label>
                                    <select
                                        value={selectedCertificate}
                                        onChange={(e) => setSelectedCertificate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {certificates.map((cert, index) => (
                                            <option key={index} value={cert}>
                                                {cert}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Пароль (если требуется)
                                    </label>
                                    <input
                                        type="password"
                                        value={certificatePassword}
                                        onChange={(e) => setCertificatePassword(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Введите пароль к сертификату"
                                    />
                                </div>

                                <button
                                    onClick={handleSignOrder}
                                    disabled={signing}
                                    className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                                >
                                    {signing ? 'Подписание...' : 'Подписать заявку'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Кнопка возврата */}
                <div className="mt-6">
                    <button
                        onClick={() => navigate('/client/orders')}
                        className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        Вернуться к заказам
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderSignaturePage;

